import { useEffect, useMemo } from "react";
import {
  Box,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Spinner,
  Text,
} from "@chakra-ui/react";
import Select from "react-select";
import { useQueryWrapper } from "services/apiHelper";
import { languageRequest } from "services/request";
import { queryKeys } from "services/querykeys";
import { type LanguageType } from "utils/constants";

interface LanguageSelection {
  languageIds: string[];
  language: string;
  usesOtherLanguage: boolean;
}

interface LanguageOption {
  value: string;
  label: string;
  language: LanguageType;
}

interface LanguageMultiSelectProps {
  languageIds?: Array<string | string[]>;
  language?: string | null;
  usesOtherLanguage?: boolean;
  label?: string;
  helperText?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  onChange: (selection: LanguageSelection) => void;
}

const OTHER_LANGUAGE_SLUG = "other-not-listed";
const LANGUAGE_CACHE_TIME = 30 * 60 * 1000;

const isOtherLanguage = (language?: LanguageType) => language?.slug === OTHER_LANGUAGE_SLUG;

const getLanguageNames = (languages: LanguageType[]) =>
  languages.map((item) => item.name).join(", ");

const normalizeLanguageIds = (languageIds: Array<string | string[]> = []) =>
  languageIds
    .flatMap((id) => (Array.isArray(id) ? id : [id]))
    .filter((id): id is string => Boolean(id));

const normalizeLanguageValue = (language?: string | null) => language ?? "";

const hasLegacyMatch = (language: LanguageType, legacyLanguage: string) => {
  const normalized = legacyLanguage.trim().toLowerCase();
  if (!normalized) return false;

  return (
    language.name.toLowerCase() === normalized ||
    (language.aliases ?? []).some((alias) => alias.toLowerCase() === normalized)
  );
};

const LanguageMultiSelect = (props: LanguageMultiSelectProps) => {
  const {
    languageIds = [],
    language = "",
    usesOtherLanguage = false,
    label = "Languages",
    helperText,
    isRequired = false,
    isDisabled = false,
    onChange,
  } = props;
  const normalizedLanguage = normalizeLanguageValue(language);
  const normalizedLanguageIds = useMemo(() => normalizeLanguageIds(languageIds), [languageIds]);

  const { data, isLoading } = useQueryWrapper(queryKeys.LANGUAGES, languageRequest.LANGUAGES, {
    staleTime: LANGUAGE_CACHE_TIME,
    cacheTime: LANGUAGE_CACHE_TIME,
    refetchOnWindowFocus: false,
  });

  const languages: LanguageType[] = data?.data ?? [];
  const options: LanguageOption[] = useMemo(
    () =>
      languages.map((item) => ({
        value: item.id,
        label: item.name,
        language: item,
      })),
    [languages],
  );

  const otherLanguage = languages.find(isOtherLanguage);
  const selectedLanguages = languages.filter((item) => normalizedLanguageIds.includes(item.id));
  const selectedOptions = options.filter(
    (option) =>
      normalizedLanguageIds.includes(option.value) ||
      (usesOtherLanguage && isOtherLanguage(option.language)),
  );
  const nonOtherSelectedLanguages = selectedLanguages.filter((item) => !isOtherLanguage(item));
  const derivedLegacyLanguage = getLanguageNames(nonOtherSelectedLanguages);
  const customLanguageValue =
    usesOtherLanguage && normalizedLanguage && normalizedLanguage !== derivedLegacyLanguage
      ? normalizedLanguage
      : "";
  const showSubmittedLanguage =
    Boolean(normalizedLanguage.trim()) && !usesOtherLanguage && selectedOptions.length === 0;

  useEffect(() => {
    if (!otherLanguage || !normalizedLanguageIds.includes(otherLanguage.id)) return;

    onChange({
      languageIds: normalizedLanguageIds.filter((id) => id !== otherLanguage.id),
      language: normalizedLanguage,
      usesOtherLanguage: true,
    });
  }, [normalizedLanguage, normalizedLanguageIds, onChange, otherLanguage]);

  useEffect(() => {
    if (
      normalizedLanguageIds.length > 0 ||
      usesOtherLanguage ||
      !normalizedLanguage.trim() ||
      languages.length === 0
    ) {
      return;
    }

    const matchedLanguage = languages.find((item) => hasLegacyMatch(item, normalizedLanguage));
    if (!matchedLanguage) return;

    onChange({
      languageIds: isOtherLanguage(matchedLanguage) ? [] : [matchedLanguage.id],
      language: matchedLanguage.name,
      usesOtherLanguage: isOtherLanguage(matchedLanguage),
    });
  }, [normalizedLanguage, normalizedLanguageIds.length, languages, onChange, usesOtherLanguage]);

  const handleSelectChange = (selected: readonly LanguageOption[] | null) => {
    const selectedItems = selected?.map((option) => option.language) ?? [];
    const nonOtherLanguages = selectedItems.filter((item) => !isOtherLanguage(item));
    const nextUsesOtherLanguage = selectedItems.some(isOtherLanguage);
    const nextLanguageIds = nonOtherLanguages.map((item) => item.id);
    const nextDerivedLanguage = getLanguageNames(nonOtherLanguages);
    const keepCustomLanguage =
      nextUsesOtherLanguage && normalizedLanguage && normalizedLanguage !== derivedLegacyLanguage
        ? normalizedLanguage
        : "";

    onChange({
      languageIds: nextLanguageIds,
      language:
        nextUsesOtherLanguage && keepCustomLanguage ? keepCustomLanguage : nextDerivedLanguage,
      usesOtherLanguage: nextUsesOtherLanguage,
    });
  };

  return (
    <FormControl id="language" isRequired={isRequired} mb={4}>
      <FormLabel mb={1}>{label}</FormLabel>
      <Select
        isMulti
        isDisabled={isDisabled}
        isLoading={isLoading}
        options={options}
        value={selectedOptions}
        placeholder="Select languages"
        onChange={handleSelectChange}
      />
      {isLoading && <Spinner size="sm" mt="2" />}
      {helperText && <FormHelperText m={0}>{helperText}</FormHelperText>}
      {showSubmittedLanguage && (
        <Box mt="3" p="3" borderWidth="1px" borderColor="#fed7aa" borderRadius="md" bg="#fff7ed">
          <Text fontSize="12px" fontWeight="bold" color="#9a3412">
            Submitted language
          </Text>
          <Text fontSize="sm" color="#7c2d12">
            {normalizedLanguage}
          </Text>
        </Box>
      )}
      {usesOtherLanguage && (
        <>
          <Input
            mt="3"
            isDisabled={isDisabled}
            value={customLanguageValue}
            placeholder="Specify language"
            onChange={(event) => {
              onChange({
                languageIds: normalizedLanguageIds,
                language: event.target.value,
                usesOtherLanguage: true,
              });
            }}
          />
          <Text mt="1" fontSize="12px" color="#7f94a6">
            This value is saved as the script language and can be resolved by an admin later.
          </Text>
        </>
      )}
    </FormControl>
  );
};

export default LanguageMultiSelect;
